import { gql } from '@apollo/client';
import { DONATION_FRAGMENT } from 'modules/donations/graphql/fragments';
import {
  DONOR_FRAGMENT,
  GROUP_FRAGMENT,
  GROUP_LIST_ITEM_FRAGMENT,
  LABWORK_FRAGMENT,
  TEAM_FRAGMENT,
  RESERVED_DONOR_NOTE_FRAGMENT,
  VITALS_PARAMETER_FRAGMENT,
  PICTURE_FRAGMENT,
  FILE_FRAGMENT,
  GROUP_TEAM_FRAGMENT,
  LABWORK_ATTACHMENT_FRAGMENT,
  DONOR_NOTE_FRAGMENT,
  DVM_EXAM_FRAGMENT,
  DONOR_BREED_FRAGMENT,
} from 'modules/donor/graphql/fragments';
import { OWNER_FRAGMENT } from 'modules/owner/graphql/fragments';
import {
  CONTACT_OUTPUT_FRAGMENT,
  ROLE_WITHOUT_PERMISSIONS_OUTPUT_FRAGMENT,
  USER_OUTPUT_FRAGMENT,
} from 'modules/user/graphql/fragments';

export const GET_DONOR_LIST_QUERY = gql`
  query getDonorsList($input: DonorsListInput!) {
    getDonorsList(input: $input) {
      data {
        ...Donor
        team {
          id
          title
        }
        user {
          ...Owner
          contacts {
            ...Contact
          }
        }
        reserveNotes {
          ...ReserveDonorNote
        }
      }
      meta {
        total
      }
    }
  }
  ${DONOR_FRAGMENT}
  ${CONTACT_OUTPUT_FRAGMENT}
  ${OWNER_FRAGMENT}
  ${RESERVED_DONOR_NOTE_FRAGMENT}
`;

export const GET_DVM_EXAM_LIST_QUERY = gql`
  query getDvmExamList($input: DvmExamInput!) {
    getDvmExamList(input: $input) {
      data {
        ...DvmExam
        performedBy {
          ...User
        }
        donor {
          ...Donor
          user {
            ...Owner
          }
        }
      }
      meta {
        total
      }
    }
  }
  ${DONOR_FRAGMENT}
  ${OWNER_FRAGMENT}
  ${DVM_EXAM_FRAGMENT}
  ${USER_OUTPUT_FRAGMENT}
`;

export const GET_PET_BREEDS_LIST = gql`
  query getPetBreedsList($input: PetBreedsListInput!) {
    getPetBreedsList(input: $input) {
      data {
        ...DonorBreed
      }
      meta {
        total
      }
    }
  }
  ${DONOR_BREED_FRAGMENT}
`;

export const GET_DVM_EXAM_QUERY = gql`
  query getDvmExam($input: GetDvmExamInput!) {
    getDvmExam(input: $input) {
      ...DvmExam
      performedBy {
        ...User
      }
      donor {
        ...Donor
        user {
          ...Owner
        }
      }
    }
  }
  ${DONOR_FRAGMENT}
  ${OWNER_FRAGMENT}
  ${DVM_EXAM_FRAGMENT}
  ${USER_OUTPUT_FRAGMENT}
`;

export const GET_DONOR_DVM_EXAMS_QUERY = gql`
  query getDonorDvmExams($input: GetDonorDvmExamsInput!) {
    getDonorDvmExams(input: $input) {
      ...DvmExam
      performedBy {
        ...User
      }
      donor {
        ...Donor
        user {
          ...Owner
        }
      }
    }
  }
  ${DONOR_FRAGMENT}
  ${OWNER_FRAGMENT}
  ${DVM_EXAM_FRAGMENT}
  ${USER_OUTPUT_FRAGMENT}
`;

//TODO: optimize this query
export const GET_DONORS_FOR_TEAM_QUERY = gql`
  query findDonorsForTeam($input: DonorsListForTeamInput!) {
    findDonorsForTeam(input: $input) {
      data {
        ...Donor
        team {
          id
          title
        }
        user {
          ...Owner
          contacts {
            ...Contact
          }
        }
        reserveNotes {
          ...ReserveDonorNote
        }
      }
      meta {
        total
      }
    }
  }
  ${DONOR_FRAGMENT}
  ${CONTACT_OUTPUT_FRAGMENT}
  ${OWNER_FRAGMENT}
  ${RESERVED_DONOR_NOTE_FRAGMENT}
`;

export const GET_DONOR_QUERY = gql`
  query getDonor($input: GetDonorInput!) {
    getDonor(input: $input) {
      ...Donor
      lastLabworkFile {
        ...File
      }
      team {
        id
        title
      }
      user {
        ...Owner
        contacts {
          ...Contact
        }
      }
      reserveNotes {
        ...ReserveDonorNote
      }
      pictures {
        ...Picture
      }
    }
  }
  ${DONOR_FRAGMENT}
  ${FILE_FRAGMENT}
  ${PICTURE_FRAGMENT}
  ${CONTACT_OUTPUT_FRAGMENT}
  ${OWNER_FRAGMENT}
  ${RESERVED_DONOR_NOTE_FRAGMENT}
`;

export const GET_CURRENT_USER_DONOR_LIST_QUERY = gql`
  query getCurrentUserDonorsList($input: CurrentUserDonorListInput!) {
    getCurrentUserDonorsList(input: $input) {
      data {
        ...Donor
        lastLabworkFile {
          ...File
        }
        team {
          id
          title
        }
        user {
          ...Owner
          contacts {
            ...Contact
          }
        }
        reserveNotes {
          ...ReserveDonorNote
        }
      }
      meta {
        total
      }
    }
  }
  ${DONOR_FRAGMENT}
  ${CONTACT_OUTPUT_FRAGMENT}
  ${OWNER_FRAGMENT}
  ${RESERVED_DONOR_NOTE_FRAGMENT}
  ${FILE_FRAGMENT}
`;

export const FIND_GROUP_LIST_QUERY = gql`
  query findAllGroup($input: GroupListInput!) {
    findAllGroup(input: $input) {
      data {
        ...GroupListItem
        teams {
          ...GroupTeam
        }
      }
      meta {
        total
      }
    }
  }
  ${GROUP_LIST_ITEM_FRAGMENT}
  ${GROUP_TEAM_FRAGMENT}
`;

export const FIND_GROUP_QUERY = gql`
  query findGroup($input: FindGroupInput!) {
    findGroup(input: $input) {
      ...Group
      teams {
        ...Team
        donors {
          ...Donor
          user {
            ...Owner
            role {
              ...RoleWithoutPermissions
            }
            contacts {
              ...Contact
            }
          }
        }
      }
      user {
        ...Owner
        role {
          ...RoleWithoutPermissions
        }
        contacts {
          ...Contact
        }
      }
    }
  }
  ${ROLE_WITHOUT_PERMISSIONS_OUTPUT_FRAGMENT}
  ${GROUP_FRAGMENT}
  ${DONOR_FRAGMENT}
  ${TEAM_FRAGMENT}
  ${CONTACT_OUTPUT_FRAGMENT}
  ${OWNER_FRAGMENT}
`;

export const GET_DONOR_LIVES_SAVED_QUERY = gql`
  query getDonorLivesSaved($input: GetLivesSavedByDonorInput!) {
    getDonorLivesSaved(input: $input) {
      lives
    }
  }
`;

export const GET_DONOR_STATS_QUERY = gql`
  query getDonorStats($input: GetDonorStatsInput!) {
    getDonorStats(input: $input) {
      totalMlsDonated
      totalDonations
      prbcCreated
      ffpUnitsCreated
    }
  }
`;

export const GET_CHARTS_DATA_QUERY_QUERY = gql`
  query getChartsData($input: GetSpecificDonorDonationsListInput!) {
    getChartsData(input: $input) {
      data {
        ...Donation
      }
      meta {
        total
      }
    }
  }
  ${DONATION_FRAGMENT}
`;

export const GET_LABWORK_LIST = gql`
  query getLabworkList($input: LabworkListInput!) {
    getLabworkList(input: $input) {
      data {
        ...Labwork
        donor {
          ...Donor
          user {
            ...Owner
          }
        }
      }
      meta {
        total
      }
    }
  }
  ${DONOR_FRAGMENT}
  ${OWNER_FRAGMENT}
  ${LABWORK_FRAGMENT}
`;

export const GET_LABWORK = gql`
  query getLabwork($input: GetLabworkInput!) {
    getLabwork(input: $input) {
      ...Labwork
      donor {
        ...Donor
        user {
          ...Owner
        }
      }
    }
  }
  ${DONOR_FRAGMENT}
  ${OWNER_FRAGMENT}
  ${FILE_FRAGMENT}
  ${LABWORK_FRAGMENT}
`;

export const GET_VITALS_PARAMETERS_LIST = gql`
  query getVitalsParameterList {
    getVitalsParameterList {
      ...VitalsParameter
    }
  }
  ${VITALS_PARAMETER_FRAGMENT}
`;

export const GET_APPOINTMENTS_DONORS_QUERY = gql`
  query getAppointmentsDonorsList {
    getAppointmentsDonorsList {
      ...Donor
      team {
        id
        title
      }
      user {
        ...Owner
        contacts {
          ...Contact
        }
      }
      reserveNotes {
        ...ReserveDonorNote
      }
    }
  }
  ${DONOR_FRAGMENT}
  ${CONTACT_OUTPUT_FRAGMENT}
  ${OWNER_FRAGMENT}
  ${RESERVED_DONOR_NOTE_FRAGMENT}
`;

export const GET_DONORS_BY_IDS_QUERY = gql`
  query getDonorsListByIds($input: GetDonorsByIdsInput!) {
    getDonorsListByIds(input: $input) {
      ...Donor
      team {
        id
        title
      }
      user {
        ...Owner
        contacts {
          ...Contact
        }
      }
      reserveNotes {
        ...ReserveDonorNote
      }
    }
  }
  ${DONOR_FRAGMENT}
  ${CONTACT_OUTPUT_FRAGMENT}
  ${OWNER_FRAGMENT}
  ${RESERVED_DONOR_NOTE_FRAGMENT}
`;

export const GET_TEAM_QUERY = gql`
  query findTeam($input: FindTeamInput!) {
    findTeam(input: $input) {
      ...Team
      donors {
        ...Donor
      }
    }
  }
  ${DONOR_FRAGMENT}
  ${TEAM_FRAGMENT}
`;

export const GET_LABWORK_ATTACHMENTS_QUERY = gql`
  query getLabworkAttachments($input: GetLabworkAttachmentsListInput!) {
    getLabworkAttachments(input: $input) {
      ...LabworkAttachment
      file {
        ...File
      }
    }
  }
  ${LABWORK_ATTACHMENT_FRAGMENT}
  ${FILE_FRAGMENT}
`;

export const GET_DONOR_RELATED_CONNECTIONS = gql`
  query hasDonorRelateddataSources($input: DeleteDonorInput!) {
    hasDonorRelateddataSources(input: $input)
  }
`;

export const GET_DONOR_APPOINTMENTS = gql`
  query getAppointmentsDatesForDonor($donorId: String!) {
    getAppointmentsDatesForDonor(donorId: $donorId) {
      id
      start
      end
    }
  }
`;

export const GET_DONORS_NOTES = gql`
  query getDonorNotes($donorId: String!) {
    getDonorNotes(donorId: $donorId) {
      ...DonorNote
    }
  }

  ${DONOR_NOTE_FRAGMENT}
`;

export const GET_DONORS_LIST_FOR_ANALYTICS_QUERY = gql`
  query getDonorsListForAnalytics($input: DonorsAnalyticsInput!) {
    getDonorsListForAnalytics(input: $input) {
      data {
        ...Donor
        team {
          id
          title
        }
        user {
          ...Owner
          contacts {
            ...Contact
          }
        }
        reserveNotes {
          ...ReserveDonorNote
        }
      }
      meta {
        total
      }
    }
  }
  ${DONOR_FRAGMENT}
  ${CONTACT_OUTPUT_FRAGMENT}
  ${OWNER_FRAGMENT}
  ${RESERVED_DONOR_NOTE_FRAGMENT}
`;

export const GET_DONOR_ANALYTICS_QUERY = gql`
  query getDonorAnalytics($input: DonorAnalyticsQueryInput!) {
    getDonorAnalytics(input: $input) {
      totalSingleDonors
      totalDoubleDonors
      totalNewDonors
      totalExperiencedDonors
      statusBreakdown {
        status
        count
      }
    }
  }
`;

export const GET_DONOR_PICTURES = gql`
  query getDonorPictures($input: GetDonorPicturesInput!) {
    getDonorPictures(input: $input) {
      ...Picture
    }
  }
  ${PICTURE_FRAGMENT}
`;

export const GET_DONOR_PIPELINE_STATS = gql`
  query getDonorPipelineStats($input: GetDonorPipelineStatsInput!) {
    getDonorPipelineStats(input: $input) {
      activeDonorCount
      pendingDonorCount
      approvedDonorCount
      registrationsByPeriod {
        period
        count
        startDate
        endDate
      }
      lastWeekDaily {
        date
        isoWeekday
        count
      }
    }
  }
`;

export const GET_RECRUITER_DASHBOARD_METRICS = gql`
  query getRecruiterDashboardMetrics(
    $input: GetRecruiterDashboardMetricsInput!
  ) {
    getRecruiterDashboardMetrics(input: $input) {
      repeatDonorCount
      activeDonorCount
      historicalActiveDonorCount
      comparedPeriodActiveDonorCount
      firstTimeDonorCount
      quarterlyDropoutCount
      quarterlyActiveAtStart
      periodDropoutCount
      periodNewEnrollmentCount
      periodActiveAtStart
      quarterlyNewEnrollmentCount
      donorsWhoDonated
      expectedDonations
      showRate
      singleDonorCount
      doubleDonorCount
      lapsedDonorCounts
      retiredDonorCounts
    }
  }
`;

export const GET_PRBC_DONATION_DONORS = gql`
  query getPrbcDonationDonors($input: GetPrbcDonationDonorsInput!) {
    getPrbcDonationDonors(input: $input) {
      donors {
        donorId
        donorShortId
        donorName
        ownerFullName
        ownerShortId
        bloodTypeDisplay
        status
        lastDonationDate
        sglDbl
        totalDonations
        periodUnits
        location
        registeredAt
        ownerPhone
        ownerEmail
        ownerCity
        ownerState
        ownerContactPreferences
        ownerAssignedLocations
        breed
        dateOfBirth
        region
        retiredDate
        retiredReason
        activeDate
        approvedDate
        archivedDate
      }
      total
    }
  }
`;

export const GET_DONOR_DONATION_HISTORY = gql`
  query getDonorDonationHistory($input: GetDonorDonationHistoryInput!) {
    getDonorDonationHistory(input: $input) {
      donations {
        id
        shortId
        date
        donorName
        locationName
        regionName
        type
        approvedML
        status
      }
      total
    }
  }
`;

export const GET_CHURN_RATE_HISTORY = gql`
  query getChurnRateHistory($input: GetChurnRateHistoryInput!) {
    getChurnRateHistory(input: $input) {
      periods {
        label
        churnRate
        dropoutCount
        enrollmentCount
        activeAtStart
        periodStart
        periodEnd
      }
    }
  }
`;

export const GET_RETENTION_HISTORY = gql`
  query getRetentionHistory($input: GetMetricsHistoryInput!) {
    getRetentionHistory(input: $input) {
      periods {
        label
        retentionScore
        repeatDonorCount
        activeDonorCount
        periodStart
        periodEnd
      }
    }
  }
`;

export const GET_DROPOUT_HISTORY = gql`
  query getDropoutHistory($input: GetMetricsHistoryInput!) {
    getDropoutHistory(input: $input) {
      periods {
        label
        dropoutRate
        dropoutCount
        activeAtStart
        periodStart
        periodEnd
      }
    }
  }
`;

export const GET_RATIO_HISTORY = gql`
  query getRatioHistory($input: GetMetricsHistoryInput!) {
    getRatioHistory(input: $input) {
      periods {
        label
        firstTimePct
        firstTimeDonorCount
        repeatDonorCount
        activeDonorCount
        periodStart
        periodEnd
      }
    }
  }
`;

export const GET_SHOW_RATE_HISTORY = gql`
  query getShowRateHistory($input: GetMetricsHistoryInput!) {
    getShowRateHistory(input: $input) {
      periods {
        label
        showRate
        donorsWhoDonated
        expectedDonations
        periodStart
        periodEnd
      }
    }
  }
`;

export const GET_MICROCHIP_PRICE = gql`
  query getMicrochipPrice {
    getMicrochipPrice {
      price
    }
  }
`;

export const GET_DONOR_CONTACT_FLAGS_QUERY = gql`
  query getDonorContactFlags($donorIds: [String!]!) {
    getDonorContactFlags(donorIds: $donorIds)
  }
`;

export const GET_DONOR_CONTACTS_QUERY = gql`
  query getDonorContacts($donorId: String!) {
    getDonorContacts(donorId: $donorId) {
      id
      donorId
      contactType
      notes
      followUpDate
      user {
        id
        firstName
        lastName
      }
      createdAt
    }
  }
`;
